import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import {
  VAPID_PUBLIC_KEY,
  urlBase64ToUint8Array,
  detectPlatform,
  isIOS,
  isStandalone,
  iosNeedsInstall,
} from '@/config/push';

export const usePushNotifications = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setIsSupported(
      typeof navigator !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
    );
  }, []);

  const saveSubscription = useCallback(
    async (subscription: PushSubscription) => {
      if (!user) return false;
      const json = subscription.toJSON() as any;

      const { error } = await supabase.from('push_subscriptions').upsert(
        {
          user_id: user.id,
          endpoint: subscription.endpoint,
          subscription: json,
          platform: detectPlatform(),
          user_agent: navigator.userAgent.slice(0, 300),
          failure_count: 0,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'endpoint' }
      );

      if (error) {
        console.error('Error saving push subscription:', error);
        return false;
      }
      return true;
    },
    [user]
  );

  const checkSubscriptionStatus = useCallback(async () => {
    if (!user || !isSupported) return;

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        setIsSubscribed(false);
        return;
      }

      const browserSubscription = await registration.pushManager.getSubscription();

      if (!browserSubscription) {
        setIsSubscribed(false);
        return;
      }

      // Keep the database row in sync with what the browser holds
      const { data: dbRow } = await supabase
        .from('push_subscriptions')
        .select('id')
        .eq('endpoint', browserSubscription.endpoint)
        .maybeSingle();

      if (!dbRow) {
        await saveSubscription(browserSubscription);
      }

      setIsSubscribed(true);
    } catch (error) {
      console.error('Error checking push subscription status:', error);
    }
  }, [user, isSupported, saveSubscription]);

  useEffect(() => {
    if (!user || !isSupported) return;
    const timeoutId = setTimeout(checkSubscriptionStatus, 1000);
    return () => clearTimeout(timeoutId);
  }, [user, isSupported, checkSubscriptionStatus]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!('Notification' in window)) return false;
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') return false;
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }, []);

  // IMPORTANT: must be called from a user gesture (required by iOS 16.4+)
  const subscribeToPush = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    if (iosNeedsInstall()) {
      toast({
        title: 'Ավելացրեք հավելվածը Home Screen-ին',
        description:
          'iOS-ում ծանուցումներն աշխատում են միայն Home Screen-ից բացված հավելվածում (Share → Add to Home Screen)։',
      });
      return false;
    }

    if (!isSupported) return false;

    try {
      setLoading(true);

      const granted = await requestPermission();
      if (!granted) {
        toast({
          title: 'Ծանուցումները արգելափակված են',
          description: 'Թույլատրեք ծանուցումները բրաուզերի կարգավորումներից։',
          variant: 'destructive',
        });
        return false;
      }

      const registration = await navigator.serviceWorker.ready;

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
        });
      }

      const saved = await saveSubscription(subscription);
      if (!saved) {
        toast({
          title: 'Սխալ',
          description: 'Չհաջողվեց պահպանել բաժանորդագրությունը։',
          variant: 'destructive',
        });
        return false;
      }

      setIsSubscribed(true);
      toast({
        title: 'Push ծանուցումները ակտիվացված են',
        description: 'Դուք կստանաք ծանուցումներ նույնիսկ փակ հավելվածի դեպքում։',
      });
      return true;
    } catch (error: any) {
      console.error('Push subscription failed:', error);
      toast({
        title: 'Սխալ',
        description: `Չհաջողվեց ակտիվացնել push ծանուցումները: ${error?.message ?? ''}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, isSupported, requestPermission, saveSubscription, toast]);

  const unsubscribeFromPush = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;

    try {
      setLoading(true);
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();

      if (subscription) {
        await supabase.from('push_subscriptions').delete().eq('endpoint', subscription.endpoint);
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      toast({
        title: 'Ծանուցումներն անջատված են',
        description: 'Այս սարքին այլևս ծանուցումներ չեն ուղարկվի։',
      });
      return true;
    } catch (error) {
      console.error('Push unsubscribe failed:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, toast]);

  const syncSubscription = useCallback(async () => {
    await checkSubscriptionStatus();
  }, [checkSubscriptionStatus]);

  const getDiagnosticInfo = useCallback(async () => {
    try {
      const registration =
        typeof navigator !== 'undefined' && 'serviceWorker' in navigator
          ? await navigator.serviceWorker.getRegistration()
          : null;
      const subscription = await registration?.pushManager.getSubscription();

      return {
        platform: detectPlatform(),
        isIOS: isIOS(),
        standalone: isStandalone(),
        iosNeedsInstall: iosNeedsInstall(),
        isSupported,
        permission: typeof Notification !== 'undefined' ? Notification.permission : 'unavailable',
        serviceWorkerReady: !!registration,
        hasBrowserSubscription: !!subscription,
        endpoint: subscription?.endpoint?.slice(0, 60),
        isSubscribed,
        userId: user?.id,
      };
    } catch (error: any) {
      return { error: error?.message };
    }
  }, [isSupported, isSubscribed, user]);

  return {
    isSupported,
    isSubscribed,
    loading,
    subscribeToPush,
    unsubscribeFromPush,
    requestPermission,
    syncSubscription,
    getDiagnosticInfo,
    checkSubscriptionStatus,
    iosNeedsInstall: iosNeedsInstall(),
  };
};
